import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";

import { CursorContextProvider } from "@/components/input/CursorContextProvider";
import { Sidebar } from "@/components/sidebar";
import { twMerge } from "@/utils/twMerge";

import { NodeFieldLabel } from "../fields/NodeTextField";

import { SymbolFieldDropdown } from "./SymbolFieldDropdown";
import { SymbolIndexField } from "./SymbolIndexField";
import { SymbolPlaceholderIndexField } from "./SymbolPlaceholderIndexField";

import type { SymbolNodeModel } from "@/models/nodes/SymbolNodeModel";

type Props = {
  node: SymbolNodeModel;
  contextId: string;
  className?: string;
  label?: string;
  withFieldDropdown?: boolean;
};

export const SymbolIndexFields = observer(function SymbolIndexFields({
  node,
  className,
  contextId,
  label,
  withFieldDropdown,
}: Props) {
  const { doc } = node;
  const labelNodes = node.sortedLabelNodes ?? [];

  const getContextId = (id: string) => `${contextId}-${id}`;

  const handleLabelMouseDown = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();

    const [labelNode] = labelNodes;

    if (!labelNode) {
      return;
    }

    runInAction(() => {
      doc.cursorContextId = getContextId(labelNode.id);
      doc.selection.resetSelection();
    });

    queueMicrotask(() => {
      const { inlineNode } = labelNode;

      if (inlineNode) {
        doc.cursor.moveTo(inlineNode, inlineNode?.nodes.length ?? 0);
      }
    });
  };

  const fieldDropdown = withFieldDropdown ? (
    <SymbolFieldDropdown
      node={node}
      className="h-6 mr-0.5"
      toggleButtonClassName="h-6 min-h-6 max-w-none"
      toggleButtonWrapperClassName="p-0"
    />
  ) : null;

  const fields = (
    <div className={twMerge("shadow-block dark:shadow-block-inverted rounded-6", className)}>
      {labelNodes.length === 0 && (
        <SymbolPlaceholderIndexField node={node} contextId={contextId} action={fieldDropdown} />
      )}
      {labelNodes.map((labelNode, index) => (
        <CursorContextProvider
          key={labelNode.id}
          id={getContextId(labelNode.id)}
          doc={doc}
          boundary={labelNode.inlineNode}
        >
          <SymbolIndexField
            node={labelNode}
            parent={node}
            leadingAction={index === 0 ? fieldDropdown : undefined}
          />
          {index !== labelNodes.length - 1 && <Sidebar.Delimiter className="m-0" />}
        </CursorContextProvider>
      ))}
    </div>
  );

  if (!label) {
    return fields;
  }

  return (
    <NodeFieldLabel
      as="div"
      label={label}
      labelPlacement="outside"
      enableFocus={false}
      onMouseDown={handleLabelMouseDown}
    >
      {fields}
    </NodeFieldLabel>
  );
});
